import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Layout, Row, Col, Breadcrumb, Collapse, Descriptions, Tag, Typography,
    Form, InputNumber, Input, Button, Space, Spin, message, Alert,
    Tabs, Table, Modal, DatePicker, Tooltip, Select, Dropdown
} from "antd";
import {
    HomeOutlined, ArrowLeftOutlined, PlusOutlined, ExperimentOutlined,
    SearchOutlined, FilterOutlined, ClearOutlined, PrinterOutlined,
    FileExcelOutlined, CheckOutlined, SafetyCertificateOutlined, EyeOutlined,
    MoreOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/axiosInstance";
import { isPermissionAllowed } from "../../../utils/permissionHelper";
import ReportPreviewModal from "../../../components/common/ReportPreviewModal";
import { buildReportPDF } from "../../../utils/reportPdfBuilder";
import { exportRowsToExcel, fmtMoney } from "../../../utils/reportExport";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { RangePicker } = DatePicker;

// Matches the server-side ceiling in output_list.php / qc_list.php
const EXPORT_LIMIT = 2000;

const PRODUCTION_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Finished": "green",
};

const APPROVAL_STATUS_COLORS = {
    "Pending": "orange",
    "Approved": "green",
};

const QC_STATE_COLORS = {
    "Pending": "orange",
    "Completed": "green",
};

const formatKg = (value) => fmtMoney(value);

const formatQty = (value) => Number(value || 0).toLocaleString();

// Combined "quantity / weight (kg)" cell used by the Input tab columns.
const formatQtyKg = (qty, kg) => `${formatQty(qty)} / ${formatKg(kg)}`;

const productLabel = (batch) => {
    if (!batch) return "-";
    if (batch.sp_code && batch.sp_name) return `${batch.sp_code} - ${batch.sp_name}`;
    return batch.sp_name || batch.sp_code || "-";
};

const FULL_REPORT_TITLE = "PRODUCTION PROCESS FULL REPORT";

// Read-only derived values are rendered as plain labels (not input boxes) so it
// is obvious they are calculated and cannot be typed into.
const ReadOnlyField = ({ label, value }) => (
    <Form.Item label={label} style={{ marginBottom: 16 }}>
        <Typography.Text
            strong
            style={{ display: "inline-block", minHeight: 32, lineHeight: "32px", fontSize: 15 }}
        >
            {value}
        </Typography.Text>
    </Form.Item>
);

const ProductionProcessDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [batchError, setBatchError] = useState(null);
    const [activeTab, setActiveTab] = useState("input");

    // Input tab - list state
    const [records, setRecords] = useState([]);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [recordPagination, setRecordPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [colSearch, setColSearch] = useState("");
    const [colDates, setColDates] = useState([]);
    const [appliedColSearch, setAppliedColSearch] = useState("");
    const [appliedColDates, setAppliedColDates] = useState([]);

    // Tab badges - outstanding work counters, independent of the tab that is
    // currently open (so both badges are correct the moment the page loads).
    const [pendingApprovalTotal, setPendingApprovalTotal] = useState(0);
    const [pendingQcTotal, setPendingQcTotal] = useState(0);

    // Pending Approval tab - list state
    const [approvals, setApprovals] = useState([]);
    const [approvalsLoading, setApprovalsLoading] = useState(false);
    const [approvalPagination, setApprovalPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [apprSearch, setApprSearch] = useState("");
    const [apprDates, setApprDates] = useState([]);
    const [appliedApprSearch, setAppliedApprSearch] = useState("");
    const [appliedApprDates, setAppliedApprDates] = useState([]);

    // Quality Control tab - list state
    const [qcRows, setQcRows] = useState([]);
    const [qcLoading, setQcLoading] = useState(false);
    const [qcPagination, setQcPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [qcSearch, setQcSearch] = useState("");
    const [qcDates, setQcDates] = useState([]);
    const [qcState, setQcState] = useState("Pending");
    const [appliedQcSearch, setAppliedQcSearch] = useState("");
    const [appliedQcDates, setAppliedQcDates] = useState([]);
    const [appliedQcState, setAppliedQcState] = useState("Pending");

    // Add Input modal
    const [inputForm] = Form.useForm();
    const [inputModalOpen, setInputModalOpen] = useState(false);
    const [inputSubmitting, setInputSubmitting] = useState(false);

    // Quality Control modal
    const [qcForm] = Form.useForm();
    const [qcTarget, setQcTarget] = useState(null);
    const [qcSubmitting, setQcSubmitting] = useState(false);

    const [approvingId, setApprovingId] = useState(null);

    // Selected input record for the "View Full Details" modal
    const [detailRecord, setDetailRecord] = useState(null);

    // Print / Excel export state
    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [printTab, setPrintTab] = useState("input");
    const [printRows, setPrintRows] = useState([]);
    const [printMode, setPrintMode] = useState("tab");
    const [printTitle, setPrintTitle] = useState("");
    const [fullReportData, setFullReportData] = useState(null);

    // Guards against stale responses when filters/tab change quickly
    const inpReqSeq = useRef(0);
    const apprReqSeq = useRef(0);
    const qcReqSeq = useRef(0);

    const canInput = isPermissionAllowed('prod_process_input');
    const canApprove = isPermissionAllowed('prod_process_approve');
    const canQc = isPermissionAllowed('prod_process_qc');
    const canExport = isPermissionAllowed('prod_process_export');
    const isOngoing = batch?.batch_status === "Ongoing";

    const outQtyWatch = Form.useWatch("out_qty", inputForm);
    const qcApprovedWatch = Form.useWatch("approved_qty", qcForm);

    // Unit weight of the batch product drives every generated weight.
    const unitKg = parseFloat(batch?.sp_unit_kg || 0);
    const previewKg = outQtyWatch && unitKg > 0
        ? Math.round(Number(outQtyWatch) * unitKg * 100) / 100
        : 0;

    // Quality control figures - only the approved quantity is entered.
    const needQty = Number(qcTarget?.needto_check_qty || 0);
    const needKg = Number(qcTarget?.needto_check_kg || 0);
    const qcUnitKg = needQty > 0 ? needKg / needQty : 0;
    const approvedQtyVal = Number(qcApprovedWatch || 0);
    const approvedKgVal = Math.round(approvedQtyVal * qcUnitKg * 100) / 100;
    const rejectedQtyVal = Math.max(0, needQty - approvedQtyVal);
    const rejectedKgVal = Math.max(0, Math.round((needKg - approvedKgVal) * 100) / 100);

    // Two quality control paths: approving everything (comment optional) or
    // approving only part of it (comment required to explain the rejection).
    const qcQtyEntered = qcApprovedWatch !== undefined && qcApprovedWatch !== null && qcApprovedWatch !== "";
    const isPartialQc = !!qcTarget && qcQtyEntered && Number(qcApprovedWatch) < needQty;

    const fetchBatch = useCallback(async () => {
        setLoading(true);
        setBatchError(null);
        try {
            const res = await api.get(`/production/detail.php?id=${id}`);
            if (res.data.status === "success") setBatch(res.data);
            else throw new Error(res.data.message || "Failed to load production batch details.");
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Failed to load production batch details.";
            setBatch(null);
            setBatchError(msg);
            message.error(msg);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchBatch();
    }, [fetchBatch]);

    // ── List loaders ──────────────────────────────────────────────────────

    const fetchRecords = useCallback(async (page = 1, limit = 10) => {
        if (!batch) return;
        const seq = ++inpReqSeq.current;
        setRecordsLoading(true);
        try {
            const res = await api.post("/production/output_list.php", {
                prodb_id: batch.prodb_id,
                approved_status: "",
                search: appliedColSearch,
                start_date: appliedColDates && appliedColDates[0] ? appliedColDates[0].format("YYYY-MM-DD") : "",
                end_date: appliedColDates && appliedColDates[1] ? appliedColDates[1].format("YYYY-MM-DD") : "",
                page,
                limit,
            });
            if (seq !== inpReqSeq.current) return;
            if (res.data.status === "success") {
                setRecords(res.data.dataset || []);
                setRecordPagination({ current: page, pageSize: limit, total: res.data.total || 0 });
            }
        } catch {
            if (seq === inpReqSeq.current) message.error("Failed to load production inputs.");
        } finally {
            if (seq === inpReqSeq.current) setRecordsLoading(false);
        }
    }, [batch, appliedColSearch, appliedColDates]);

    const fetchApprovals = useCallback(async (page = 1, limit = 10) => {
        if (!batch) return;
        const seq = ++apprReqSeq.current;
        setApprovalsLoading(true);
        try {
            const res = await api.post("/production/output_list.php", {
                prodb_id: batch.prodb_id,
                approved_status: "Pending",
                search: appliedApprSearch,
                start_date: appliedApprDates && appliedApprDates[0] ? appliedApprDates[0].format("YYYY-MM-DD") : "",
                end_date: appliedApprDates && appliedApprDates[1] ? appliedApprDates[1].format("YYYY-MM-DD") : "",
                page,
                limit,
            });
            if (seq !== apprReqSeq.current) return;
            if (res.data.status === "success") {
                setApprovals(res.data.dataset || []);
                setApprovalPagination({ current: page, pageSize: limit, total: res.data.total || 0 });
            }
        } catch {
            if (seq === apprReqSeq.current) message.error("Failed to load pending approvals.");
        } finally {
            if (seq === apprReqSeq.current) setApprovalsLoading(false);
        }
    }, [batch, appliedApprSearch, appliedApprDates]);

    const fetchQc = useCallback(async (page = 1, limit = 10) => {
        if (!batch) return;
        const seq = ++qcReqSeq.current;
        setQcLoading(true);
        try {
            const res = await api.post("/production/qc_list.php", {
                prodb_id: batch.prodb_id,
                qc_state: appliedQcState,
                search: appliedQcSearch,
                start_date: appliedQcDates && appliedQcDates[0] ? appliedQcDates[0].format("YYYY-MM-DD") : "",
                end_date: appliedQcDates && appliedQcDates[1] ? appliedQcDates[1].format("YYYY-MM-DD") : "",
                page,
                limit,
            });
            if (seq !== qcReqSeq.current) return;
            if (res.data.status === "success") {
                setQcRows(res.data.dataset || []);
                setQcPagination({ current: page, pageSize: limit, total: res.data.total || 0 });
            }
        } catch {
            if (seq === qcReqSeq.current) message.error("Failed to load quality control records.");
        } finally {
            if (seq === qcReqSeq.current) setQcLoading(false);
        }
    }, [batch, appliedQcState, appliedQcSearch, appliedQcDates]);

    // Outstanding approvals / QC for the tab badges. Loaded on mount and after
    // every action, so the counts never depend on which tab was opened first.
    const fetchCounts = useCallback(async () => {
        if (!batch) return;
        try {
            const [apprRes, qcRes] = await Promise.all([
                api.post("/production/output_list.php", {
                    prodb_id: batch.prodb_id,
                    approved_status: "Pending",
                    page: 1,
                    limit: 1,
                }),
                api.post("/production/qc_list.php", {
                    prodb_id: batch.prodb_id,
                    qc_state: "Pending",
                    page: 1,
                    limit: 1,
                }),
            ]);
            setPendingApprovalTotal(Number(apprRes.data?.total || 0));
            setPendingQcTotal(Number(qcRes.data?.total || 0));
        } catch {
            // Badge counts are informational - leave the last known values.
        }
    }, [batch]);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    // Load the active tab whenever the tab, filters or batch change
    useEffect(() => {
        if (!batch) return;
        if (activeTab === "input") {
            fetchRecords(1, recordPagination.pageSize);
        } else if (activeTab === "approval") {
            fetchApprovals(1, approvalPagination.pageSize);
        } else {
            fetchQc(1, qcPagination.pageSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batch, activeTab, appliedColSearch, appliedColDates,
        appliedApprSearch, appliedApprDates, appliedQcSearch, appliedQcDates, appliedQcState]);

    // ── Filters ───────────────────────────────────────────────────────────

    const handleColFilter = () => {
        setAppliedColSearch(colSearch.trim());
        setAppliedColDates(colDates);
    };
    const handleColClear = () => {
        setColSearch("");
        setColDates([]);
        setAppliedColSearch("");
        setAppliedColDates([]);
    };

    const handleApprFilter = () => {
        setAppliedApprSearch(apprSearch.trim());
        setAppliedApprDates(apprDates);
    };
    const handleApprClear = () => {
        setApprSearch("");
        setApprDates([]);
        setAppliedApprSearch("");
        setAppliedApprDates([]);
    };

    const handleQcFilter = () => {
        setAppliedQcSearch(qcSearch.trim());
        setAppliedQcDates(qcDates);
        setAppliedQcState(qcState);
    };
    const handleQcClear = () => {
        setQcSearch("");
        setQcDates([]);
        setQcState("Pending");
        setAppliedQcSearch("");
        setAppliedQcDates([]);
        setAppliedQcState("Pending");
    };

    // ── Actions ───────────────────────────────────────────────────────────

    const refreshAll = (tab) => {
        fetchBatch();
        fetchCounts();
        if (tab === "input" || tab === undefined) fetchRecords(1, recordPagination.pageSize);
        if (tab === "approval" || tab === undefined) fetchApprovals(1, approvalPagination.pageSize);
        if (tab === "qc" || tab === undefined) fetchQc(1, qcPagination.pageSize);
    };

    const handleInputSubmit = async () => {
        try {
            const values = await inputForm.validateFields();
            setInputSubmitting(true);
            const res = await api.post("/production/output_create.php", {
                prodb_id: batch.prodb_id,
                out_qty: values.out_qty,
            });
            if (res.data.status === "success") {
                message.success(`Production input added - ${formatKg(res.data.out_kg)} kg generated.`);
                setInputModalOpen(false);
                inputForm.resetFields();
                // Refresh the list and the Pending Approval badge together.
                refreshAll("input");
            } else {
                throw new Error(res.data.message);
            }
        } catch (err) {
            if (err?.errorFields) return;
            message.error(err?.response?.data?.message || "Failed to add production input.");
        } finally {
            setInputSubmitting(false);
        }
    };

    const handleApprove = (record) => {
        Modal.confirm({
            title: "Approve Production Input",
            content: `Approve ${formatQty(record.out_qty)} qty / ${formatKg(record.out_kg)} kg for ${batch.prodb_ref}? The batch actual quantities will be increased and the input will move to Quality Control.`,
            okText: "Yes, Approve",
            okType: "primary",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setApprovingId(record.prodout_id);
                try {
                    const res = await api.post("/production/output_approve.php", {
                        prodb_id: batch.prodb_id,
                        prodout_id: record.prodout_id,
                    });
                    if (res.data.status === "success") {
                        message.success(res.data.message || "Production input approved successfully.");
                        refreshAll();
                    } else {
                        throw new Error(res.data.message);
                    }
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to approve production input.");
                } finally {
                    setApprovingId(null);
                }
            },
        });
    };

    const openQcModal = (record) => {
        setQcTarget(record);
        qcForm.setFieldsValue({
            approved_qty: Number(record.needto_check_qty || 0),
            checked_comment: "",
        });
    };

    const handleQcSubmit = async () => {
        try {
            const values = await qcForm.validateFields();
            setQcSubmitting(true);
            const res = await api.post("/production/qc_create.php", {
                prodb_id: batch.prodb_id,
                prodout_id: qcTarget.prodout_id,
                approved_qty: values.approved_qty,
                checked_comment: values.checked_comment || "",
            });
            if (res.data.status === "success") {
                message.success(res.data.message || "Quality control recorded successfully.");
                setQcTarget(null);
                qcForm.resetFields();
                refreshAll();
            } else {
                throw new Error(res.data.message);
            }
        } catch (err) {
            if (err?.errorFields) return;
            message.error(err?.response?.data?.message || "Failed to record quality control.");
        } finally {
            setQcSubmitting(false);
        }
    };

    // ── Print & Excel export (respects applied filters; no filters = all) ──

    // Raw fetch of every row matching the tab's applied filters (the server
    // caps the page size, so the wrapper below reports truncation).
    const fetchRowsForExportRaw = async (tab) => {
        if (tab === "input" || tab === "approval") {
            const res = await api.post("/production/output_list.php", {
                prodb_id: batch.prodb_id,
                approved_status: tab === "approval" ? "Pending" : "",
                search: tab === "approval" ? appliedApprSearch : appliedColSearch,
                start_date: tab === "approval"
                    ? (appliedApprDates && appliedApprDates[0] ? appliedApprDates[0].format("YYYY-MM-DD") : "")
                    : (appliedColDates && appliedColDates[0] ? appliedColDates[0].format("YYYY-MM-DD") : ""),
                end_date: tab === "approval"
                    ? (appliedApprDates && appliedApprDates[1] ? appliedApprDates[1].format("YYYY-MM-DD") : "")
                    : (appliedColDates && appliedColDates[1] ? appliedColDates[1].format("YYYY-MM-DD") : ""),
                page: 1,
                limit: EXPORT_LIMIT,
            });
            return res.data?.dataset || [];
        }
        const res = await api.post("/production/qc_list.php", {
            prodb_id: batch.prodb_id,
            qc_state: appliedQcState,
            search: appliedQcSearch,
            start_date: appliedQcDates && appliedQcDates[0] ? appliedQcDates[0].format("YYYY-MM-DD") : "",
            end_date: appliedQcDates && appliedQcDates[1] ? appliedQcDates[1].format("YYYY-MM-DD") : "",
            page: 1,
            limit: EXPORT_LIMIT,
        });
        return res.data?.dataset || [];
    };

    const fetchRowsForExport = async (tab) => {
        const rows = await fetchRowsForExportRaw(tab);
        if (rows.length >= EXPORT_LIMIT) {
            message.warning(
                `Only the first ${EXPORT_LIMIT} matching records were exported. Narrow the filters to export the rest.`
            );
        }
        return rows;
    };

    const exportColumns = (tab) => {
        if (tab === "qc") {
            return [
                { title: "Logged Date", dataIndex: "log_dt", width: 18 },
                { title: "Logged By", dataIndex: "log_by", width: 18 },
                { title: "Need To Check Qty", dataIndex: "need_qty", width: 16 },
                { title: "Need To Check Kg", dataIndex: "need_kg", width: 15 },
                { title: "Approved Qty", dataIndex: "approved_qty", width: 13 },
                { title: "Approved Kg", dataIndex: "approved_kg", width: 13 },
                { title: "Rejected Qty", dataIndex: "rejected_qty", width: 13 },
                { title: "Rejected Kg", dataIndex: "rejected_kg", width: 13 },
                { title: "Status", dataIndex: "qc_state", width: 12 },
                { title: "Comment", dataIndex: "comment", width: 26 },
                { title: "Checked By", dataIndex: "checked_by", width: 18 },
                { title: "Checked Date", dataIndex: "checked_dt", width: 18 },
            ];
        }
        if (tab === "input") {
            return [
                { title: "Logged Date", dataIndex: "log_dt", width: 18 },
                { title: "Logged By", dataIndex: "log_by", width: 18 },
                { title: "Output Qty/Kg", dataIndex: "out_qty_kg", width: 16 },
                { title: "Approved Qty/Kg", dataIndex: "appr_qty_kg", width: 17 },
                { title: "QC Approved Qty/Kg", dataIndex: "qc_approved_qty_kg", width: 19 },
                { title: "QC Reject Qty/Kg", dataIndex: "qc_rejected_qty_kg", width: 17 },
                { title: "Status", dataIndex: "approved_status", width: 12 },
                { title: "Approved By", dataIndex: "chk_by", width: 18 },
                { title: "Approved Date", dataIndex: "chk_dt", width: 18 },
                { title: "Checked By", dataIndex: "checked_by", width: 18 },
                { title: "Checked Date", dataIndex: "checked_dt", width: 18 },
            ];
        }
        return [
            { title: "Logged Date", dataIndex: "log_dt", width: 18 },
            { title: "Logged By", dataIndex: "log_by", width: 18 },
            { title: "Output Qty", dataIndex: "out_qty", width: 12 },
            { title: "Output Weight (Kg)", dataIndex: "out_kg", width: 16 },
            { title: "Status", dataIndex: "approved_status", width: 12 },
            { title: "Approved By", dataIndex: "chk_by", width: 18 },
            { title: "Approved Date", dataIndex: "chk_dt", width: 18 },
        ];
    };

    const buildExportRows = (tab, rows) => {
        if (tab === "qc") {
            return rows.map((r) => ({
                log_dt: r.prod_log_dt ? dayjs(r.prod_log_dt).format("YYYY-MM-DD HH:mm") : "-",
                log_by: r.prod_log_by_name || "-",
                need_qty: formatQty(r.needto_check_qty),
                need_kg: formatKg(r.needto_check_kg),
                approved_qty: r.qc_state === "Completed" ? formatQty(r.approved_qty) : "-",
                approved_kg: r.qc_state === "Completed" ? formatKg(r.approved_kg) : "-",
                rejected_qty: r.qc_state === "Completed" ? formatQty(r.rejected_qty) : "-",
                rejected_kg: r.qc_state === "Completed" ? formatKg(r.rejected_kg) : "-",
                qc_state: r.qc_state || "-",
                comment: r.checked_comment || "-",
                checked_by: r.checked_by_name || "-",
                checked_dt: r.checked_dt ? dayjs(r.checked_dt).format("YYYY-MM-DD HH:mm") : "-",
            }));
        }
        if (tab === "input") {
            return rows.map((r) => ({
                log_dt: r.prod_log_dt ? dayjs(r.prod_log_dt).format("YYYY-MM-DD HH:mm") : "-",
                log_by: r.prod_log_by_name || "-",
                out_qty_kg: formatQtyKg(r.out_qty, r.out_kg),
                appr_qty_kg: r.approved_status === "Approved" ? formatQtyKg(r.out_qty, r.out_kg) : "-",
                qc_approved_qty_kg: r.qc_state === "Completed" ? formatQtyKg(r.approved_qty, r.approved_kg) : "-",
                qc_rejected_qty_kg: r.qc_state === "Completed" ? formatQtyKg(r.rejected_qty, r.rejected_kg) : "-",
                approved_status: r.approved_status || "-",
                chk_by: r.prod_chk_by_name || "-",
                chk_dt: r.prod_chk_dt ? dayjs(r.prod_chk_dt).format("YYYY-MM-DD HH:mm") : "-",
                checked_by: r.checked_by_name || "-",
                checked_dt: r.checked_dt ? dayjs(r.checked_dt).format("YYYY-MM-DD HH:mm") : "-",
            }));
        }
        return rows.map((r) => ({
            log_dt: r.prod_log_dt ? dayjs(r.prod_log_dt).format("YYYY-MM-DD HH:mm") : "-",
            log_by: r.prod_log_by_name || "-",
            out_qty: formatQty(r.out_qty),
            out_kg: formatKg(r.out_kg),
            approved_status: r.approved_status || "-",
            chk_by: r.prod_chk_by_name || "-",
            chk_dt: r.prod_chk_dt ? dayjs(r.prod_chk_dt).format("YYYY-MM-DD HH:mm") : "-",
        }));
    };

    const tabTitle = (tab) => {
        if (tab === "input") return "PRODUCTION INPUT REPORT";
        if (tab === "approval") return "PRODUCTION PENDING APPROVAL REPORT";
        return "PRODUCTION QUALITY CONTROL REPORT";
    };

    const buildPrintDoc = async (tab, rows) => {
        const cols = exportColumns(tab);
        const data = buildExportRows(tab, rows);
        return buildReportPDF({
            title: tabTitle(tab),
            subtitle: `Production Batch ${batch.prodb_ref}`,
            columns: cols.map((c) => ({ header: c.title })),
            rows: data.map((r) => cols.map((c) => r[c.dataIndex] ?? "")),
            summary: [
                { label: "Batch Ref", value: batch.prodb_ref },
                { label: "Product", value: productLabel(batch) },
                { label: "Status", value: batch.batch_status || "-" },
                { label: "Records", value: String(rows.length) },
            ],
            orientation: "landscape",
            columnStyles: tab === "qc"
                ? { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } }
                : { 2: { halign: "right" }, 3: { halign: "right" } },
        });
    };

    const handlePrint = async () => {
        if (!batch) return;
        const tab = activeTab;
        setPrinting(true);
        try {
            const rows = await fetchRowsForExport(tab);
            if (rows.length === 0) {
                message.warning("No data to print.");
                return;
            }
            setPrintMode("tab");
            setPrintTitle(tabTitle(tab));
            setPrintTab(tab);
            setPrintRows(rows);
            setPrintVisible(true);
            setPdfLoading(true);
            setPdfDataUri(null);
            const doc = await buildPrintDoc(tab, rows);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
            setPrinting(false);
        }
    };

    // Full detail report: batch info, progress & quality summary, then every
    // input / pending approval / quality control record in one document.
    const rowMatrix = (tab, rows) => {
        const cols = exportColumns(tab);
        const data = buildExportRows(tab, rows);
        return { cols, matrix: data.map((r) => cols.map((c) => r[c.dataIndex] ?? "")) };
    };

    const OUT_RIGHT = { 2: { halign: "right" }, 3: { halign: "right" } };
    const QC_RIGHT = { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } };

    const buildFullReportDoc = async ({ inputs, approvals, qc }) => {
        const inputPart = rowMatrix("input", inputs);
        const approvalPart = rowMatrix("approval", approvals);
        const qcPart = rowMatrix("qc", qc);

        return buildReportPDF({
            title: FULL_REPORT_TITLE,
            subtitle: `Production Batch ${batch.prodb_ref}`,
            orientation: "landscape",
            recordCount: inputs.length + approvals.length + qc.length,
            infoBlocks: [
                {
                    title: "Batch Information",
                    items: [
                        { label: "Batch Ref", value: batch.prodb_ref },
                        { label: "Status", value: batch.batch_status || "-" },
                        { label: "Sales Product", value: productLabel(batch) },
                        { label: "Processed By", value: batch.processed_by_name || "-" },
                        { label: "Unit Weight (Kg)", value: unitKg > 0 ? formatKg(unitKg) : "-" },
                        { label: "Expire Date", value: batch.expire_date ? dayjs(batch.expire_date).format("YYYY-MM-DD") : "-" },
                        { label: "Production Start Date", value: batch.prod_start_date ? dayjs(batch.prod_start_date).format("YYYY-MM-DD") : "-" },
                        { label: "Production End Date", value: batch.prod_end_date ? dayjs(batch.prod_end_date).format("YYYY-MM-DD") : "-" },
                        { label: "Created By", value: batch.creator_name || "-" },
                        { label: "Created Date", value: batch.cr_dt ? dayjs(batch.cr_dt).format("YYYY-MM-DD HH:mm") : "-" },
                        { label: "Last Updated By", value: batch.updater_name || "-" },
                        { label: "Last Updated Date", value: batch.lc_dt ? dayjs(batch.lc_dt).format("YYYY-MM-DD HH:mm") : "-" },
                    ],
                },
                {
                    title: "Production Progress & Quality Summary",
                    items: [
                        { label: "Target Quantity", value: formatQty(batch.target_qty) },
                        { label: "Target Weight (Kg)", value: formatKg(batch.target_kg) },
                        { label: "Actual Quantity", value: formatQty(batch.actual_qty) },
                        { label: "Actual Weight (Kg)", value: formatKg(batch.actual_kg) },
                        { label: "QC Approved Quantity", value: formatQty(batch.qc_approved_qty) },
                        { label: "QC Approved Weight (Kg)", value: formatKg(batch.qc_approved_kg) },
                        { label: "QC Rejected Quantity", value: formatQty(batch.qc_rejecteded_qty) },
                        { label: "QC Rejected Weight (Kg)", value: formatKg(batch.qc_rejecteded_kg) },
                        { label: "Target Achievement", value: `${achievementPercent}%` },
                    ],
                },
            ],
            sections: [
                {
                    title: "Production Input Records",
                    columns: inputPart.cols.map((c) => ({ header: c.title })),
                    rows: inputPart.matrix,
                    columnStyles: OUT_RIGHT,
                },
                {
                    title: "Pending Approval Records",
                    columns: approvalPart.cols.map((c) => ({ header: c.title })),
                    rows: approvalPart.matrix,
                    columnStyles: OUT_RIGHT,
                },
                {
                    title: "Quality Control Records",
                    columns: qcPart.cols.map((c) => ({ header: c.title })),
                    rows: qcPart.matrix,
                    columnStyles: QC_RIGHT,
                },
            ],
        });
    };

    // Pull every record (all statuses) for the full report, ignoring tab filters.
    const fetchAllRowsForFullReport = async () => {
        const [inpRes, apprRes, qcRes] = await Promise.all([
            api.post("/production/output_list.php", { prodb_id: batch.prodb_id, approved_status: "", page: 1, limit: EXPORT_LIMIT }),
            api.post("/production/output_list.php", { prodb_id: batch.prodb_id, approved_status: "Pending", page: 1, limit: EXPORT_LIMIT }),
            api.post("/production/qc_list.php", { prodb_id: batch.prodb_id, qc_state: "", page: 1, limit: EXPORT_LIMIT }),
        ]);
        const datasets = [
            inpRes.data?.dataset || [],
            apprRes.data?.dataset || [],
            qcRes.data?.dataset || [],
        ];
        if (datasets.some((d) => d.length >= EXPORT_LIMIT)) {
            message.warning(
                `Only the first ${EXPORT_LIMIT} records of some sections were included. Narrow the filters for a full export.`
            );
        }
        return { inputs: datasets[0], approvals: datasets[1], qc: datasets[2] };
    };

    const handlePrintFullReport = async () => {
        if (!batch) return;
        setPrinting(true);
        try {
            const data = await fetchAllRowsForFullReport();
            if (data.inputs.length === 0 && data.approvals.length === 0 && data.qc.length === 0) {
                message.warning("No data to print.");
                return;
            }
            setPrintMode("full");
            setPrintTitle(FULL_REPORT_TITLE);
            setFullReportData(data);
            setPrintVisible(true);
            setPdfLoading(true);
            setPdfDataUri(null);
            const doc = await buildFullReportDoc(data);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
            setPrinting(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            if (printMode === "full") {
                if (!fullReportData) return;
                const doc = await buildFullReportDoc(fullReportData);
                doc.save(`production-full-${batch.prodb_ref}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            } else {
                const doc = await buildPrintDoc(printTab, printRows);
                doc.save(`production-${printTab}-${batch.prodb_ref}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            }
            message.success("PDF downloaded successfully.");
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Failed to download PDF.");
        }
    };

    const handleExportExcel = async () => {
        if (!batch) return;
        const tab = activeTab;
        setExporting(true);
        try {
            const rows = await fetchRowsForExport(tab);
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }
            exportRowsToExcel({
                filename: `production-${tab}-${batch.prodb_ref}`,
                sheetName: tab === "input" ? "Production Input" : tab === "approval" ? "Pending Approval" : "Quality Control",
                columns: exportColumns(tab),
                rows: buildExportRows(tab, rows),
            });
            message.success("Excel exported successfully.");
        } catch (err) {
            console.error("Excel export failed:", err);
            message.error("Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    // ── Collapsible batch details ─────────────────────────────────────────

    const targetKg = parseFloat(batch?.target_kg || 0);
    const actualKg = parseFloat(batch?.actual_kg || 0);
    const qcApprovedKgTotal = parseFloat(batch?.qc_approved_kg || 0);
    const qcRejectedKgTotal = parseFloat(batch?.qc_rejecteded_kg || 0);
    const achievementPercent = targetKg > 0 ? Math.min(100, Math.round((actualKg / targetKg) * 100)) : 0;

    const collapseItems = batch ? [
        {
            key: "info",
            label: (
                <Space>
                    <Typography.Text strong>Batch Information</Typography.Text>
                    <Tag color={PRODUCTION_STATUS_COLORS[batch.batch_status] || "default"} style={{ fontWeight: 600 }}>
                        {batch.batch_status || "-"}
                    </Tag>
                </Space>
            ),
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Batch Ref" span={2}>
                        <Typography.Text strong>{batch.prodb_ref}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Sales Product" span={2}>{productLabel(batch)}</Descriptions.Item>
                    <Descriptions.Item label="Processed By">{batch.processed_by_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Unit Weight (Kg)">{unitKg > 0 ? formatKg(unitKg) : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Production Start Date">
                        {batch.prod_start_date ? dayjs(batch.prod_start_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Production End Date">
                        {batch.prod_end_date ? dayjs(batch.prod_end_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Expire Date">
                        {batch.expire_date ? dayjs(batch.expire_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created By">{batch.creator_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Created Date">
                        {batch.cr_dt ? dayjs(batch.cr_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated By">{batch.updater_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Last Updated Date">
                        {batch.lc_dt ? dayjs(batch.lc_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
        {
            key: "summary",
            label: <Typography.Text strong>Production Progress &amp; Quality Summary</Typography.Text>,
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Target Quantity">{formatQty(batch.target_qty)}</Descriptions.Item>
                    <Descriptions.Item label="Target Weight (Kg)">{formatKg(batch.target_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Actual Quantity">{formatQty(batch.actual_qty)}</Descriptions.Item>
                    <Descriptions.Item label="Actual Weight (Kg)">{formatKg(batch.actual_kg)}</Descriptions.Item>
                    <Descriptions.Item label="QC Approved Quantity">{formatQty(batch.qc_approved_qty)}</Descriptions.Item>
                    <Descriptions.Item label="QC Approved Weight (Kg)">{formatKg(batch.qc_approved_kg)}</Descriptions.Item>
                    <Descriptions.Item label="QC Rejected Quantity">{formatQty(batch.qc_rejecteded_qty)}</Descriptions.Item>
                    <Descriptions.Item label="QC Rejected Weight (Kg)">{formatKg(batch.qc_rejecteded_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Target Achievement" span={2}>
                        <Typography.Text strong>{achievementPercent}%</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {formatKg(actualKg)} of {formatKg(targetKg)} kg produced,{" "}
                            {formatKg(qcApprovedKgTotal)} kg approved / {formatKg(qcRejectedKgTotal)} kg rejected
                        </Typography.Text>
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
    ] : [];

    // ── Tables ────────────────────────────────────────────────────────────

    const inputColumns = [
        {
            title: "Logged Date", dataIndex: "prod_log_dt", key: "prod_log_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Logged By", dataIndex: "prod_log_by_name", key: "prod_log_by_name", render: (val) => val || "-" },
        { title: "Output Qty/Kg", key: "out_qty_kg", align: "right", render: (_, r) => formatQtyKg(r.out_qty, r.out_kg) },
        {
            // Approval-stage quantity: the output figure once the input is approved.
            title: "Approved Qty/Kg", key: "input_approved", align: "right",
            render: (_, r) => (r.approved_status === "Approved" ? formatQtyKg(r.out_qty, r.out_kg) : "-"),
        },
        {
            title: "QC Approved Qty/Kg", key: "qc_approved", align: "right",
            render: (_, r) => (r.qc_state === "Completed" ? formatQtyKg(r.approved_qty, r.approved_kg) : "-"),
        },
        {
            title: "QC Reject Qty/Kg", key: "qc_rejected", align: "right",
            render: (_, r) => (r.qc_state === "Completed" ? formatQtyKg(r.rejected_qty, r.rejected_kg) : "-"),
        },
        {
            title: "Status", dataIndex: "approved_status", key: "approved_status", align: "center",
            render: (val) => (
                <Tag color={APPROVAL_STATUS_COLORS[val] || "default"} style={{ fontWeight: 600 }}>{val || "-"}</Tag>
            ),
        },
        { title: "Approved By", dataIndex: "prod_chk_by_name", key: "prod_chk_by_name", render: (val) => val || "-" },
        {
            title: "Approved Date", dataIndex: "prod_chk_dt", key: "prod_chk_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Checked By", dataIndex: "checked_by_name", key: "checked_by_name", render: (val) => val || "-" },
        {
            title: "Checked Date", dataIndex: "checked_dt", key: "checked_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        {
            title: "Action", key: "action", align: "center", width: 70,
            render: (_, record) => (
                <Dropdown
                    trigger={["click"]}
                    menu={{
                        items: [
                            { key: "view", label: "View Full Details", icon: <EyeOutlined /> },
                        ],
                        onClick: ({ key }) => {
                            if (key === "view") setDetailRecord(record);
                        },
                    }}
                >
                    <Button type="text" size="small" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    const approvalColumns = [
        {
            title: "Logged Date", dataIndex: "prod_log_dt", key: "prod_log_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Logged By", dataIndex: "prod_log_by_name", key: "prod_log_by_name", render: (val) => val || "-" },
        { title: "Output Qty", dataIndex: "out_qty", key: "out_qty", align: "right", render: (val) => formatQty(val) },
        { title: "Output Weight (Kg)", dataIndex: "out_kg", key: "out_kg", align: "right", render: (val) => formatKg(val) },
        {
            title: "Action", key: "action", align: "center", width: 130,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    className="fluid-btn-primary"
                    loading={approvingId === record.prodout_id}
                    onClick={() => handleApprove(record)}
                >
                    Approve
                </Button>
            ),
        },
    ];

    const qcColumns = [
        {
            title: "Logged Date", dataIndex: "prod_log_dt", key: "prod_log_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Logged By", dataIndex: "prod_log_by_name", key: "prod_log_by_name", render: (val) => val || "-" },
        { title: "Need To Check Qty", dataIndex: "needto_check_qty", key: "needto_check_qty", align: "right", render: (val) => formatQty(val) },
        { title: "Need To Check (Kg)", dataIndex: "needto_check_kg", key: "needto_check_kg", align: "right", render: (val) => formatKg(val) },
        {
            title: "Approved Qty", dataIndex: "approved_qty", key: "approved_qty", align: "right",
            render: (val, r) => (r.qc_state === "Completed" ? formatQty(val) : "-"),
        },
        {
            title: "Approved (Kg)", dataIndex: "approved_kg", key: "approved_kg", align: "right",
            render: (val, r) => (r.qc_state === "Completed" ? formatKg(val) : "-"),
        },
        {
            title: "Rejected Qty", dataIndex: "rejected_qty", key: "rejected_qty", align: "right",
            render: (val, r) => (r.qc_state === "Completed" ? formatQty(val) : "-"),
        },
        {
            title: "Rejected (Kg)", dataIndex: "rejected_kg", key: "rejected_kg", align: "right",
            render: (val, r) => (r.qc_state === "Completed" ? formatKg(val) : "-"),
        },
        {
            title: "Status", dataIndex: "qc_state", key: "qc_state", align: "center",
            render: (val) => (
                <Tag color={QC_STATE_COLORS[val] || "default"} style={{ fontWeight: 600 }}>{val || "-"}</Tag>
            ),
        },
        { title: "Checked By", dataIndex: "checked_by_name", key: "checked_by_name", render: (val) => val || "-" },
        {
            title: "Checked Date", dataIndex: "checked_dt", key: "checked_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        {
            title: "Comment", dataIndex: "checked_comment", key: "checked_comment",
            width: 220, ellipsis: true, render: (val) => val || "-",
        },
        {
            title: "Action", key: "action", align: "center", width: 140,
            render: (_, record) => (
                record.qc_state === "Pending" ? (
                    <Button
                        type="primary"
                        size="small"
                        icon={<SafetyCertificateOutlined />}
                        className="fluid-btn-primary"
                        onClick={() => openQcModal(record)}
                    >
                        Record QC
                    </Button>
                ) : <Typography.Text type="secondary">Checked</Typography.Text>
            ),
        },
    ];

    // ── Tabs ──────────────────────────────────────────────────────────────

    const exportButtons = canExport ? (
        <>
            <Tooltip title="Print (respects current filters)">
                <Button icon={<PrinterOutlined />} onClick={handlePrint} loading={printing}>Print</Button>
            </Tooltip>
            <Tooltip title="Export to Excel (respects current filters)">
                <Button
                    icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                    onClick={handleExportExcel}
                    loading={exporting}
                >
                    Excel
                </Button>
            </Tooltip>
        </>
    ) : null;

    const tabItems = [
        {
            key: "input",
            label: "Input",
            children: (
                <>
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={colSearch}
                                    onChange={(e) => setColSearch(e.target.value)}
                                    onPressEnter={handleColFilter}
                                    placeholder="Logged by"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Date Range:</div>
                                <RangePicker value={colDates} onChange={setColDates} style={{ width: "100%" }} />
                            </Col>
                            <Col xs={24} sm={24} lg={14}>
                                <div className="fluid-filter-compact-actions" style={{ flexWrap: "wrap", height: "auto", minHeight: 32, rowGap: 8 }}>
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleColFilter} className="fluid-btn-primary">
                                        Filter
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleColClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                    {canInput && isOngoing && (
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => setInputModalOpen(true)}
                                            className="fluid-btn-primary"
                                        >
                                            Add Input
                                        </Button>
                                    )}
                                    {exportButtons}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={inputColumns}
                            dataSource={records}
                            rowKey="prodout_id"
                            loading={recordsLoading}
                            bordered
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={{
                                current: recordPagination.current,
                                pageSize: recordPagination.pageSize,
                                total: recordPagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pag) => fetchRecords(pag.current, pag.pageSize)}
                        />
                    </div>
                </>
            ),
        },
        {
            key: "approval",
            label: (
                <Space size={6}>
                    Pending Approval
                    {pendingApprovalTotal > 0 && <Tag color="orange">{pendingApprovalTotal}</Tag>}
                </Space>
            ),
            children: (
                <>
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={apprSearch}
                                    onChange={(e) => setApprSearch(e.target.value)}
                                    onPressEnter={handleApprFilter}
                                    placeholder="Logged by"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Date Range:</div>
                                <RangePicker value={apprDates} onChange={setApprDates} style={{ width: "100%" }} />
                            </Col>
                            <Col xs={24} sm={24} lg={14}>
                                <div className="fluid-filter-compact-actions" style={{ flexWrap: "wrap", height: "auto", minHeight: 32, rowGap: 8 }}>
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleApprFilter} className="fluid-btn-primary">
                                        Filter
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleApprClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                    {exportButtons}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={canApprove ? approvalColumns : approvalColumns.filter((c) => c.key !== "action")}
                            dataSource={approvals}
                            rowKey="prodout_id"
                            loading={approvalsLoading}
                            bordered
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={{
                                current: approvalPagination.current,
                                pageSize: approvalPagination.pageSize,
                                total: approvalPagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pag) => fetchApprovals(pag.current, pag.pageSize)}
                        />
                    </div>
                </>
            ),
        },
        {
            key: "qc",
            label: (
                <Space size={6}>
                    Quality Control
                    {pendingQcTotal > 0 && <Tag color="orange">{pendingQcTotal}</Tag>}
                </Space>
            ),
            children: (
                <>
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={qcSearch}
                                    onChange={(e) => setQcSearch(e.target.value)}
                                    onPressEnter={handleQcFilter}
                                    placeholder="Logged / checked by"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>QC Status:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={qcState}
                                    onChange={setQcState}
                                    options={[
                                        { value: "Pending", label: "Pending QC" },
                                        { value: "Completed", label: "Completed" },
                                        { value: "", label: "All" },
                                    ]}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Date Range:</div>
                                <RangePicker value={qcDates} onChange={setQcDates} style={{ width: "100%" }} />
                            </Col>
                            <Col xs={24} sm={24} lg={12}>
                                <div className="fluid-filter-compact-actions" style={{ flexWrap: "wrap", height: "auto", minHeight: 32, rowGap: 8 }}>
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleQcFilter} className="fluid-btn-primary">
                                        Filter
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleQcClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                    {exportButtons}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={canQc ? qcColumns : qcColumns.filter((c) => c.key !== "action")}
                            dataSource={qcRows}
                            rowKey="prodout_id"
                            loading={qcLoading}
                            bordered
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={{
                                current: qcPagination.current,
                                pageSize: qcPagination.pageSize,
                                total: qcPagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pag) => fetchQc(pag.current, pag.pageSize)}
                        />
                    </div>
                </>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Production' },
                                { title: 'Production Process' },
                                { title: batch?.prodb_ref || 'Details' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ExperimentOutlined /> Production Process
                        </h1>
                        <p className="fluid-page-subtitle">
                            {batch
                                ? `Record inputs, approvals and quality control for ${batch.prodb_ref}`
                                : "Production batch details"}
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <Space>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate('/production/production-process')}
                                className="fluid-btn-secondary"
                            >
                                Back to Batches
                            </Button>
                            {batch && (
                                <Tooltip title="Print full production report (batch info, progress & quality summary, all records)">
                                    <Button
                                        type="primary"
                                        icon={<PrinterOutlined />}
                                        onClick={handlePrintFullReport}
                                        loading={printing}
                                        className="fluid-btn-primary"
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <Spin spinning={loading}>
                    {!loading && batchError && (
                        <div className="fluid-card">
                            <Alert
                                type="error"
                                showIcon
                                message="Could not load this production batch"
                                description={batchError}
                                action={<Button size="small" onClick={fetchBatch}>Retry</Button>}
                            />
                        </div>
                    )}
                    {batch && (
                        <>
                            <div className="fluid-card" style={{ marginBottom: 16 }}>
                                <Collapse
                                    items={collapseItems}
                                    defaultActiveKey={["info", "summary"]}
                                    expandIconPosition="end"
                                />
                            </div>

                            {!(canInput || canApprove || canQc) && (
                                <div className="fluid-card" style={{ marginBottom: 16 }}>
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message="Read only access"
                                        description="You can view the batch details above, but you do not have permission to add inputs, approve them or record quality control."
                                    />
                                </div>
                            )}

                            <div className="fluid-card">
                                <div style={{ padding: "16px 16px 0" }}>
                                    <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                                </div>
                            </div>
                        </>
                    )}
                </Spin>
            </Content>

            <Footerdiv />

            {/* Add Production Input modal */}
            <Modal
                title={<Space><PlusOutlined style={{ color: "#1677ff" }} /> Add Production Input</Space>}
                open={inputModalOpen}
                onCancel={() => { setInputModalOpen(false); inputForm.resetFields(); }}
                footer={null}
                width={560}
                maskClosable={false}
                forceRender
            >
                <Form form={inputForm} layout="vertical" onFinish={handleInputSubmit}>
                    <Form.Item
                        name="out_qty"
                        label="Input Quantity"
                        rules={[{ required: true, message: "Please enter the Input quantity" }]}
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            min={1}
                            precision={0}
                            placeholder="e.g. 100"
                        />
                    </Form.Item>
                    <Form.Item label="Output Weight (Kg)">
                        <Input
                            readOnly
                            value={previewKg ? formatKg(previewKg) : "0.00"}
                        />
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                            {unitKg > 0
                                ? `Auto-calculated: quantity \u00d7 ${unitKg} kg per unit`
                                : "This batch product has no unit weight configured."}
                        </Typography.Text>
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setInputModalOpen(false); inputForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={inputSubmitting}>
                                    Add Input
                                </Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Quality Control modal */}
            <Modal
                title={<Space><SafetyCertificateOutlined style={{ color: "#1677ff" }} /> Record Quality Control</Space>}
                open={!!qcTarget}
                onCancel={() => { setQcTarget(null); qcForm.resetFields(); }}
                footer={null}
                width={600}
                maskClosable={false}
                forceRender
            >
                <Form form={qcForm} layout="vertical" onFinish={handleQcSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <ReadOnlyField label="Quantity Sent For QC" value={formatQty(needQty)} />
                        </Col>
                        <Col span={12}>
                            <ReadOnlyField label="Weight Sent For QC (Kg)" value={formatKg(needKg)} />
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="approved_qty"
                                label="QC Approved Quantity"
                                rules={[
                                    { required: true, message: "Please enter the approved quantity" },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === null || value === "") return Promise.resolve();
                                            const num = Number(value);
                                            if (num < 0) return Promise.reject(new Error("Approved quantity cannot be negative"));
                                            if (num > needQty) return Promise.reject(new Error("Approved quantity cannot exceed the quantity sent for QC"));
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <InputNumber style={{ width: "100%" }} min={0} max={needQty} precision={0} placeholder="e.g. 100" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <ReadOnlyField label="QC Approved Weight (Kg)" value={formatKg(approvedKgVal)} />
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <ReadOnlyField label="QC Rejected Quantity" value={formatQty(rejectedQtyVal)} />
                        </Col>
                        <Col span={12}>
                            <ReadOnlyField label="QC Rejected Weight (Kg)" value={formatKg(rejectedKgVal)} />
                        </Col>
                    </Row>
                    <Form.Item
                        name="checked_comment"
                        label="Comment"
                        rules={[
                            { max: 100, message: "Comment cannot exceed 100 characters" },
                            {
                                // A partial approval must always be justified.
                                validator: (_, value) => {
                                    if (isPartialQc && !String(value || "").trim()) {
                                        return Promise.reject(new Error("Please give a reason for the rejected quantity"));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input.TextArea rows={2} maxLength={100} showCount placeholder="Add a comment" />
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setQcTarget(null); qcForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<CheckOutlined />} loading={qcSubmitting}>
                                    Save QC Result
                                </Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Full details of a single production input record */}
            <Modal
                title={<Space><EyeOutlined style={{ color: "#1677ff" }} /> Production Input - Full Details</Space>}
                open={!!detailRecord}
                onCancel={() => setDetailRecord(null)}
                footer={null}
                width={760}
                maskClosable={false}
            >
                {detailRecord && (
                    <div>
                        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                            Input Details
                        </Typography.Title>
                        <Descriptions bordered size="small" column={2} style={{ marginBottom: 20 }}>
                            <Descriptions.Item label="Logged Date">
                                {detailRecord.prod_log_dt ? dayjs(detailRecord.prod_log_dt).format("YYYY-MM-DD HH:mm") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Logged By">{detailRecord.prod_log_by_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Output Qty/Kg">{formatQtyKg(detailRecord.out_qty, detailRecord.out_kg)}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={APPROVAL_STATUS_COLORS[detailRecord.approved_status] || "default"} style={{ fontWeight: 600 }}>
                                    {detailRecord.approved_status || "-"}
                                </Tag>
                            </Descriptions.Item>
                        </Descriptions>

                        <Typography.Title level={5} style={{ marginBottom: 12 }}>
                            Approval
                        </Typography.Title>
                        <Descriptions bordered size="small" column={2} style={{ marginBottom: 20 }}>
                            <Descriptions.Item label="Approved Qty/Kg">
                                {detailRecord.approved_status === "Approved" ? formatQtyKg(detailRecord.out_qty, detailRecord.out_kg) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Approved By">{detailRecord.prod_chk_by_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Approved Date" span={2}>
                                {detailRecord.prod_chk_dt ? dayjs(detailRecord.prod_chk_dt).format("YYYY-MM-DD HH:mm") : "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Typography.Title level={5} style={{ marginBottom: 12 }}>
                            Quality Control
                        </Typography.Title>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="QC Status">
                                <Tag color={QC_STATE_COLORS[detailRecord.qc_state] || "default"} style={{ fontWeight: 600 }}>
                                    {detailRecord.qc_state || "-"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="QC Approved Qty/Kg">
                                {detailRecord.qc_state === "Completed" ? formatQtyKg(detailRecord.approved_qty, detailRecord.approved_kg) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="QC Reject Qty/Kg">
                                {detailRecord.qc_state === "Completed" ? formatQtyKg(detailRecord.rejected_qty, detailRecord.rejected_kg) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Checked By">{detailRecord.checked_by_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Checked Date" span={2}>
                                {detailRecord.checked_dt ? dayjs(detailRecord.checked_dt).format("YYYY-MM-DD HH:mm") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="QC Comment" span={2}>{detailRecord.checked_comment || "-"}</Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Modal>

            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title={printTitle || tabTitle(printTab)}
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                showFiltersOption={false}
                onDownload={handleDownloadPDF}
            />
        </Layout>
    );
};

export default ProductionProcessDetail;
